import { useEffect, useState } from "react";
import QRCode from "qrcode";

/**
 * A URL, as something a phone camera can read off a screen.
 *
 * Extracted from phase 6's `JoinInvite` when phase 8 needed a second one — the
 * recap link at the end of a game. Two encoders configured slightly differently
 * is how one of them ends up scanning on the phone it was tested with and on no
 * others.
 *
 * # Why the dependency
 *
 * Reed–Solomon over GF(256) plus mask-pattern scoring is exactly the fiddly,
 * easy-to-get-subtly-wrong work the dependency-minimalism rule carves an
 * exception for. SVG rather than canvas so it scales to whatever size the
 * television is and stays crisp.
 *
 * # It can fail, and that is not an error screen
 *
 * A code that will not render draws nothing. Every caller shows the URL or a
 * room code in text beside it, because somebody is always on the wrong network
 * or holding a phone whose camera app does not scan.
 */
export function QrCode({
  url,
  className,
  label = "QR code",
}: {
  url: string;
  className?: string;
  /** For the accessible name — the *destination*, not "QR code". */
  label?: string;
}) {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void QRCode.toString(url, {
      type: "svg",
      // High correction: this is read off a screen at an angle, across a room,
      // possibly with somebody's head in the way.
      errorCorrectionLevel: "H",
      margin: 1,
      // Light-on-dark, matching the rest of the board. A scanner does not care
      // which way round it is; a room in the dark does.
      color: { dark: "#ffffff", light: "#00000000" },
    }).then(
      (rendered) => {
        if (!cancelled) {
          setSvg(rendered);
        }
      },
      () => {
        if (!cancelled) {
          setSvg(null);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!svg) {
    return null;
  }

  return (
    <div
      data-testid="qr-code"
      role="img"
      aria-label={label}
      className={className}
      // The markup comes from the QR encoder, over a URL the page built from its
      // own origin and its own route parameter — no user input reaches it.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
